<template>
  <div class="collection-cards-view">
    <align flex collapse-end direction="flex-start">
      <shared-filters
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <align small-gap class="collection-options">
        <align small-gap>
          <icon-button
            icon="list"
            :class="{ success: collectionViewType === 'cards' }"
            @click="collectionViewType = 'cards'"
          />

          <icon-button
            icon="view_module"
            :class="{ success: collectionViewType === 'images' }"
            @click="collectionViewType = 'images'"
          />
        </align>

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      is-filtering
      :filters="filters"
      :results="numResults"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results with your filters"
        description="Try adjusting your filters"
      />

      <template>
        <div v-if="collectionViewType === 'images'" class="grid">
          <collection-grid-card
            v-for="card in collectionCards"
            :key="card.id"
            as="router-link"
            :to="{ path: '/collection', query: { ...$route.query, cardId: card.collectionCardId, profile: 'collection' } }"
            :card="card"
          />
        </div>

        <div v-else class="list">
          <card-list-item
            v-for="card in collectionCards"
            :key="card.id"
            as="router-link"
            :to="{ path: '/collection', query: { ...$route.query, cardId: card.collectionCardId, profile: 'collection' } }"
            :card="card"
            :fields="getFields(card)"
            selectable
            :selected="selectedCardIds.includes(card.collectionCardId)"
            @toggleSelected="toggleSelectedCard(card.collectionCardId)"
          >
            <verified-icon
              v-if="card.cardId"
              v-tooltip="{ content: 'Verified Profile', delay: { show: 1500, hide: 100 }, placement: 'bottom' }"
            />

            <collection-card-badges class="medium-up" :card="card" small />
          </card-list-item>
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/vue';

import { searchService } from '@/services';

import { DEFAULT_COLLECTION_FILTERS, COLLECTION_SORT_OPTIONS } from '@cardladder/shared/constants';
import { toImageUrl } from '@cardladder/shared/utils/format.util';

import CardListItem from '@cardladder/shared/components/CardListItem';
import CollectionCardBadges from '@cardladder/shared/components/CollectionCardBadges';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';
import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';
import CollectionGridCard from './CollectionGridCard';

export default {
  components: {
    CardListItem,
    CollectionGridCard,
    CollectionCardBadges,
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
    VerifiedIcon,
  },

  data() {
    return {
      limit: 20,
      loaded: false,
      collectionCards: [],
      page: 1,
      hasMoreData: true,
      loading: false,
      numResults: 0,
      defaultFilters: DEFAULT_COLLECTION_FILTERS,
      sortOptions: COLLECTION_SORT_OPTIONS,
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  mounted() {
    this.$bus.$on('collection-changed', this.handleCollectionChanged);
  },

  beforeDestroy() {
    this.$bus.$off('collection-changed', this.handleCollectionChanged);
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.loadCards();
      }
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
    }),

    collectionViewType: {
      get() {
        return this.$store.state.collectionViewType;
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_VIEW_TYPE', value);
      },
    },

    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.collectionSort);

        if (activeSort) {
          return this.$store.state.collectionSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.collectionDirection;
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_DIRECTION', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.collectionFilters;
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_FILTERS', value);
      },
    },

    query: {
      get() {
        return this.$store.state.collectionQuery;
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_QUERY', value);
      },
    },

    selectedCardIds: {
      get() {
        return this.$store.state.collectionSelectedIds;
      },

      set(value) {
        this.$store.commit('SET_COLLECTION_SELECTED_IDS', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.collectionCards.length === 0;
    },

    hasFilters() {
      return !isEqual(this.filters, this.defaultFilters);
    },
  },

  methods: {
    async reset() {
      this.loaded = false;
      this.page = 1;
      this.hasMoreData = true;
      this.selectedCardIds = [];

      try {
        await this.loadCards(true);
      } catch (e) {
        this.$toasted.error('Error loading the collection');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },

    async loadCards(resetLoad = false) {
      this.loading = true;

      const filters = {
        ...this.filters,
        collectionId: this.collection.id,
      };

      try {
        const data = await searchService.search({
          index: 'collectioncards',
          query: this.query.trim(),
          page: this.page,
          limit: this.limit,
          filters,
          sort: this.sort,
          direction: this.direction,
        });

        this.numResults = data.totalHits;

        const cards = data.hits.map((hit) => ({
          ...hit,
          image: hit.cardId && (!hit.imageCustom || !hit.imageCustom.includes('collections')) ? toImageUrl(`cards/${this.collectionViewType === 'cards' ? 'thumb_' : ''}${hit.cardId}`) : hit.imageCustom || hit.image,
        }));

        if (resetLoad) {
          this.collectionCards = cards;
        } else {
          this.collectionCards = [...this.collectionCards, ...cards];
        }

        if (cards.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        this.$toasted.error('Error loading the cards');
        Sentry.captureException(e);
      }

      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },

    toggleSelectedCard(id) {
      if (this.selectedCardIds.indexOf(id) >= 0) {
        this.selectedCardIds.splice(this.selectedCardIds.indexOf(id), 1);
      } else {
        this.selectedCardIds.push(id);
      }
    },

    getFields(card) {
      const fields = card.sold && this.filters.sold === 'sold'
        ? ['investment', 'profit', 'priceSold']
        : ['priceMovement', 'profit', 'currentValue'];

      if (this.collectionViewType === 'images') {
        fields.splice(this.sort === 'priceMovement' ? 1 : 0, 1);
      }

      return fields.sort((a, b) => {
        if (a === this.sort) return -1;
        if (b === this.sort) return 1;

        return 0;
      }).reverse();
    },

    handleCollectionChanged(change) {
      if (!change) {
        this.reset();
      } else if (change.remove) {
        const index = this.collectionCards.findIndex(({ collectionCardId }) => collectionCardId === change.remove);
        if (index !== -1) this.collectionCards.splice(index, 1);
      } else if (change.edit) {
        const index = this.collectionCards.findIndex(({ collectionCardId }) => collectionCardId === change.edit.id);
        if (index !== -1) this.$set(this.collectionCards, index, { ...this.collectionCards[index], ...change.edit });
      } else if (change.add) {
        setTimeout(() => {
          this.reset();
        }, 5000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.results {
  position: relative;
  flex: 1;
}

.results {
  margin-top: $gp;
}

.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(1, minmax(0, 1fr));

  @media($small-up) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .panel-open & {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  }

  @media($large-up) {
    grid-template-columns: repeat(4, minmax(0, 1fr));

    .panel-open & {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  @media($xlarge-up) {
    grid-template-columns: repeat(4, minmax(0, 1fr));

    .panel-open & {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  @media($xxlarge-up) {
    grid-template-columns: repeat(6, minmax(0, 1fr));

    .panel-open & {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
  }

  .collection-card-badges {
    margin-top: $gp * .5;
  }
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}
</style>
